/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useFormikContext } from 'formik';

// locals
import {
  preventNonNumeric,
  preventOnlySpecialChars,
  preventSpecialChars
} from 'app/utils/miscellaneous';
import { FormatUtils } from 'app/utils';
import useCustomDebounce from './use-custom-debounce';

const useInputChangeHandler = handleChange => {
  const { values, setFieldValue, setFieldTouched, getFieldMeta, validateField } =
    useFormikContext();

  /*  ########################### Filter Form Inputs ########################################### */

  // handle filter input changes
  const handleFilterInputChange = e => {
    setFieldValue(e.target.name, e.target.value);
    handleChange && handleChange(e);
  };

  // handle non-numeric filter form input fields change
  const preventNonNumericFilterInput = e => preventNonNumeric(e, handleFilterInputChange);

  // handle switch toggle
  const handleFilterToggleSwitch = ({ target: { name, value } }) => {
    handleFilterInputChange({
      target: { name, value: value !== 'true' }
    });
  };

  /*  ########################### CRUD Form Inputs ########################################### */

  // handle crud form input change
  const handleInputChange = e => {
    setFieldValue(e.target.name, e.target.value);
  };

  // handle datepicker input changes
  const handleDatePickerChange = e => {
    setFieldValue(e.name, e.value);
    handleChange && handleChange(e);
  };

  // handle non-numeric input fields change
  const preventNonNumericInput = e => preventNonNumeric(e, handleInputChange);

  // handle non-special and non-numeric input fields change
  const preventNonSpecialCharsInput = e => preventSpecialChars(e, handleInputChange);

  // handle only non-special input fields change
  const preventOnlyNonSpecialCharsInput = e => preventOnlySpecialChars(e, handleInputChange);

  // handle switch toggle
  const handleToggleSwitch = ({ target: { name, value } }) => {
    handleInputChange({
      target: { name, value: value !== 'true' }
    });
  };

  /*  ########################### Other Form Inputs - Functions ######################################## */

  // debounced filter input value change method
  const [debouncedVal, setDebouncedVal] = useCustomDebounce({});

  useEffect(() => {
    handleChange && handleChange(debouncedVal);
  }, [debouncedVal]);

  const handleDebouncedValue = e => {
    setFieldValue(e.target.name, e.target.value);
    setDebouncedVal({ name: e.target.name, value: e.target.value.trim() });
  };

  // debounced number only field change
  const preventNonNumericDebounceInput = e => preventNonNumeric(e, handleDebouncedValue);

  // debounced letter only field change
  const preventNonSpecialDebounceInput = e => preventSpecialChars(e, handleDebouncedValue);

  // debounced letter and number only field change
  const preventOnlyNonSpecialDebounceCharsInput = e =>
    preventOnlySpecialChars(e, handleInputChange);

  // iban field change method
  const handleIbanInput = e => {
    const val = e.target.value;
    const formattedVal = val.replace(/\s/g, '').replace(/[_/]/g, '');
    setFieldValue(e.target.name, formattedVal);
  };

  // masked phone field input change method
  const handlePhoneInput = e => {
    const val = e.target.value;
    const name = e.target.name;
    const formattedVal = val
      .replace(/\s/g, '')
      .replace('90', '')
      .replace(/_/g, '')
      .replace('+', '')
      .replace('(', '')
      .replace(')', '');
    setFieldValue(name, formattedVal);
    handleChange && setDebouncedVal({ name, value: formattedVal });
  };

  // currency input change method
  const handleCurrencyInput = (name, val) => {
    !val?.floatValue ? setFieldValue(name, null) : setFieldValue(name, val?.floatValue);
  };

  // year picker input change method
  const handleYearPickerChange = e => {
    const year = FormatUtils.convertYearOnly(e);
    const val = { name: 'year', value: year.toString() };

    setFieldValue(val.name, val.value);
    handleChange && handleChange(val);
  };

  /*  ########################### Other Form - Functions (onBlur etc.) ######################################## */

  const handleFieldTouched = e => {
    if (e.target.value) validateField(e.target.name);
  };

  return {
    values,
    setFieldValue,
    setFieldTouched,
    getFieldMeta,
    handleFilterInputChange,
    handleInputChange,
    preventNonNumericFilterInput,
    preventNonNumericInput,
    preventNonNumericDebounceInput,
    preventNonSpecialCharsInput,
    preventOnlyNonSpecialCharsInput,
    preventNonSpecialDebounceInput,
    preventOnlyNonSpecialDebounceCharsInput,
    handleFilterToggleSwitch,
    handleToggleSwitch,
    handleDatePickerChange,
    handleIbanInput,
    handlePhoneInput,
    handleCurrencyInput,
    handleDebouncedValue,
    handleYearPickerChange,
    handleFieldTouched
  };
};

export default useInputChangeHandler;
